import 'core-js/stable';
import 'regenerator-runtime/runtime';

import Vue from 'vue';
import { AuthOnBehalfOfConfig } from '@andromeda/polaris-data-access-products';
import { ImageWithMetadata } from '@andromeda/calvera-ui';
import App from '@/App.vue';

// components
import { MxrCardCarousel } from '@/components/card-carousel';
import { MxrCallToAction } from '@/components/call-to-action';
import { MxrFooter } from '@/components/footer';
import { MxrPromotions } from '@/components/promotions';
import { MxrTabs } from '@/components/tabs';
import { MxrRetailerLocations } from '@/components/retailer-locations';
import store from '@/store';

// models
import { MxrConfig } from '@/interfaces/MxrConfig';
import { IBedData } from '@/interfaces/BedData';
import { queryString, setupRetailerTakeover } from '@/utils';
import {
	FETCH_MXR_RETAILER_LOCATIONS,
	GET_ALL_BASE_INGREDIENTS,
	GET_CAROUSEL_BASE_INGREDIENTS,
	GET_MXR_RETAILER_LOCATIONS,
	SET_BETA,
	SET_INTEGRATED_RETAILER_LOCATIONS,
	SET_KROGER_AUTH_CONFIG,
	SET_LOADING_PROMOTIONS,
	SET_MXR_CONFIG,
	SET_MXR_JSON_DATA,
	SET_MXR_OFFERS,
	SET_MXR_PROMOTIONS,
	SET_MXR_RETAILER,
	SET_MXR_RETAILER_LOCATIONS,
	SET_TAKEOVER,
	SET_TRACKING_CONFIGS,
} from '@/store/types';
import { addMessageHandlers } from '@/utils/MessageEventHandler';
import { Takeover, TakeoverCollection } from '@/takeover';
import { Partner } from '@/interfaces/Partner';

Vue.config.productionTip = false;
// These environment variables - process.env.VUE_XXX
// are not supplied anywhere in the production build
// so krogerAuthConfig comes up undefined at runtime
// const baseUri = process.env.VUE_APP_KROGER_BASE_URI;
// const scope = process.env.VUE_APP_KROGER_SCOPE;
// const clientId = process.env.VUE_APP_KROGER_CLIENT_ID;
// const responseType = process.env.VUE_APP_KROGER_RESPONSE_TYPE;
// const redirectUri = process.env.VUE_APP_KROGER_REDIRECT_URI;

// Kroger Auth
// TODO: Move to affiliate-data-access-kroger
const baseUri = 'https://api.kroger.com/v1/connect/oauth2/authorize';
const scope = 'cart.basic:write';
const clientId = 'groceryserver-polaris-26dedce8586f2431e2030f08e75d6107';
const responseType = 'code';
const redirectUri = 'https://products.polaris.me/v1/integrations/kro/auth/callback';
const krogerAuthConfig = {
	baseUri,
	scope,
	clientId,
	responseType,
	redirectUri,
} as AuthOnBehalfOfConfig;

const mxr = (json: string) => {
	new Vue({
		store,
		components: {
			MxrCardCarousel,
			MxrCallToAction,
			MxrFooter,
			MxrPromotions,
			MxrTabs,
			MxrRetailerLocations,
		},
		async created() {
			this.$store.commit(SET_KROGER_AUTH_CONFIG, krogerAuthConfig);
			const bedData = JSON.parse(json) as IBedData;
			this.$store.commit(SET_MXR_JSON_DATA, bedData);
			const promotion = MxrConfig.create(bedData);
			const beta = queryString.getBeta();
			if (beta && beta.length) {
				this.$store.commit(SET_BETA, true);
			}

			// eslint-disable-next-line camelcase
			window.sm_params = window.sm_params || {};

			const takeover = TakeoverCollection.get(promotion.id);
			const zip =
				queryString.getZipCode() || window.sm_params.zipCode || '';
			const maxp = queryString.getMax();
			const max = parseInt(maxp, 10) || bedData.maxStoreLocations;
			if (takeover) {
				this.$store.commit(SET_LOADING_PROMOTIONS, true);
				this.$store.commit(SET_TAKEOVER, takeover);
				this.$store.dispatch(FETCH_MXR_RETAILER_LOCATIONS, {
					zip,
					max,
					takeover,
					host: promotion.host
				});
				const promo = setupRetailerTakeover(promotion, takeover);
				this.$store.commit(SET_MXR_CONFIG, promo);
			}

			this.$store.dispatch(SET_TRACKING_CONFIGS, { host: promotion.host });
			this.$store.commit(SET_MXR_CONFIG, promotion);

			if (!takeover && promotion.retailerIntegration) {
				this.$store.commit(SET_LOADING_PROMOTIONS, true);
				await this.$store.dispatch(SET_INTEGRATED_RETAILER_LOCATIONS, {
					zip,
					max,
					promotion,
				});
				await this.$store.dispatch(SET_MXR_PROMOTIONS, {
					zipcode: queryString.getZipCodeQueryParam(),
				});
				// todo: may no longer be needed after all RI completed:
				const tkv = new Takeover(
					promotion.id,
					promotion.integratedRetailer as Partner,
					promotion.integratedRetailer,
					5,
					new ImageWithMetadata(100, 100, '', bedData.footerLogo.url),
					this.$store.getters[GET_ALL_BASE_INGREDIENTS],
					this.$store.getters[GET_CAROUSEL_BASE_INGREDIENTS],
					new Map<number, number>([])
				);
				this.$store.commit(SET_TAKEOVER, tkv);
				await this.$store.dispatch(SET_MXR_RETAILER_LOCATIONS, {
					promotion,
					takeover: tkv,
				});
				await this.$store.dispatch(
					SET_MXR_RETAILER,
					this.$store.getters[GET_MXR_RETAILER_LOCATIONS][0]
				);
			} else {
				this.$store.dispatch(SET_LOADING_PROMOTIONS);
			}
			addMessageHandlers(this.$store);
		},
		render: (h: any) => h(App),
	}
).$mount('#mxr');
};

declare global {
	interface Window {
		initMxr: (json: string) => void;
	}
}

window.initMxr = window.initMxr || mxr;
